import { signal } from '@angular/core';

export interface LocaleConfig {
  code: string;
  name: string;
  nativeName: string;
}

export const DEFAULT_LOCALE = 'nb';

export const SUPPORTED_LOCALES: LocaleConfig[] = [
  { code: 'nb', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'en', name: 'English', nativeName: 'English' },
];

const STORAGE_KEY = 'tabify_locale';

// Codes written by the old Angular i18n build, still present in returning browsers.
const LEGACY_LOCALES: Record<string, string> = {
  'nb-NO': 'nb',
  'en-US': 'en',
};

export function isLocaleSupported(code: string): boolean {
  return SUPPORTED_LOCALES.some((locale) => locale.code === code);
}

function readStoredLocale(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return DEFAULT_LOCALE;
  }

  const migrated = LEGACY_LOCALES[stored];
  if (migrated) {
    localStorage.setItem(STORAGE_KEY, migrated);
    return migrated;
  }

  return isLocaleSupported(stored) ? stored : DEFAULT_LOCALE;
}

// Resolved at import time so the locale is already correct on pages that never
// render the language selector (/callback, /thank-you) and before the first HTTP call.
const locale = signal<string>(readStoredLocale());

export const currentLocale = locale.asReadonly();

export function setLocale(code: string): void {
  if (!isLocaleSupported(code)) {
    return;
  }

  locale.set(code);

  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, code);
  }
}
